import { useEffect, useMemo, useState } from "react";
import { Badge, Button, Table } from "react-bootstrap";
import { FaBoxOpen, FaCertificate, FaEye, FaImage } from "react-icons/fa";
import { Link } from "react-router-dom";
import { useTranslate } from "../../../hooks/LanguageProvider";
import DocumentPreviewModal, {
  PreviewableDocument,
} from "../../common/DocumentPreviewModal";
import { buildPreviewableDocument } from "../../../utilities/uploadFiles";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5002";
const oemProductsImageUrl = `${backendUrl}/uploads/oem-products/`;

type Product = {
  id_product: number;
  product_name?: string;
  category?: string;
  material?: string;
  annual_capacity?: number | string;
  unit?: string;
  constructor_name?: string;
  homologation_status?: string;
  export_status?: string;
  product_images?: string[] | string;
};

type Certification = {
  id: number;
  certification_name?: string;
  certificate_number?: string;
  expiry_date?: string;
  linked_product?: string;
  conformity_status?: string;
  export_impact?: string;
  certificate_file?: string;
  original_file_name?: string;
};

type Props = {
  userId?: number | string | null;
  view?: "products" | "certifications";
};

const normalizeImages = (value: Product["product_images"]): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(String).filter(Boolean);

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
  } catch {
    return String(value)
      .split("|||")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const getProductImageUrl = (path?: string) => {
  const filename = String(path || "").split("/").pop();
  return filename ? `${oemProductsImageUrl}${filename}` : "";
};

const displayValue = (value: unknown, fallback: string) => {
  if (value === null || value === undefined || value === "") return fallback;
  return String(value);
};

export default function ProductsTab({ userId, view = "products" }: Props) {
  const { translate } = useTranslate();
  const missingText = translate("not_provided") || "Non renseigné";
  const managePath =
    view === "products"
      ? `/OEMProducts?id_user=${userId || ""}`
      : `/OEMCertifications?id_user=${userId || ""}`;

  const [products, setProducts] = useState<Product[]>([]);
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewFile, setPreviewFile] = useState<PreviewableDocument | null>(
    null,
  );

  const stats = useMemo(
    () => ({
      products: products.length,
      certifications: certifications.length,
    }),
    [certifications.length, products.length]
  );

  useEffect(() => {
    if (!userId) {
      setProducts([]);
      setCertifications([]);
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    async function loadAssets() {
      setLoading(true);

      try {
        if (view === "products") {
          setCertifications([]);

          const productsResponse = await fetch(`${backendUrl}/api/oems/${userId}/products`, {
            signal: controller.signal,
          });
          const productsData = productsResponse.ok ? await productsResponse.json() : [];

          setProducts(Array.isArray(productsData) ? productsData : []);
          return;
        }

        setProducts([]);

        const certificationsResponse = await fetch(`${backendUrl}/api/oems/${userId}/certifications`, {
          signal: controller.signal,
        });
        const certificationsData = certificationsResponse.ok ? await certificationsResponse.json() : [];

        setCertifications(Array.isArray(certificationsData) ? certificationsData : []);
      } catch (error) {
        if (!controller.signal.aborted) {
          if (view === "products") {
            setProducts([]);
          } else {
            setCertifications([]);
          }
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    loadAssets();

    return () => {
      controller.abort();
    };
  }, [userId, view]);

  return (
    <div className="card border-0 shadow-sm rounded-4">
      <style>{`
        .oem-products-tab {
          color: #0f172a;
        }

        .oem-products-tab .text-value,
        .oem-products-tab td {
          color: #0f172a !important;
          font-weight: 650;
        }

        .oem-products-tab th {
          color: #111827;
          font-size: 13px;
          white-space: nowrap;
        }

        .oem-products-tab .asset-thumb,
        .oem-products-tab .asset-thumb-empty {
          width: 46px;
          height: 46px;
          border-radius: 8px;
          object-fit: cover;
          border: 1px solid #e5e7eb;
          background: #f8fafc;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .oem-products-tab .missing-chip {
          display: inline-flex;
          align-items: center;
          min-height: 24px;
          padding: 3px 9px;
          border-radius: 999px;
          background: #fff7ed;
          border: 1px solid #fed7aa;
          color: #9a3412;
          font-size: 12px;
          font-weight: 800;
          white-space: nowrap;
        }

        .oem-products-tab .tab-title-row {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        .oem-products-tab .tab-title-icon {
          width: 34px;
          height: 34px;
          border-radius: 10px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #fff7d6;
          color: #d89b00;
          flex: 0 0 auto;
        }

        .oem-products-tab .tab-title-icon.certification {
          background: #eef2ff;
          color: #4f46e5;
        }
      `}</style>

      <div className="card-body p-4 oem-products-tab">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
          <div className="tab-title-row">
            <span className={`tab-title-icon ${view === "certifications" ? "certification" : ""}`}>
              {view === "products" ? <FaBoxOpen /> : <FaCertificate />}
            </span>
            <h4 className="fw-semibold mb-0">
              {view === "products" ? translate("products") : translate("certifications")}
            </h4>
            <Badge bg="light" text="dark" pill className="border">
              {view === "products" ? stats.products : stats.certifications}
            </Badge>
          </div>
          <Button
            as={Link as any}
            to={managePath}
            className="border-0 px-4 py-2"
            style={{
              backgroundColor: "#facc15",
              color: "#111827",
              fontWeight: 600,
              borderRadius: 10,
            }}
          >
            {view === "products"
              ? translate("manage_product")
              : translate("manage_certification")}
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-warning" role="status" />
          </div>
        ) : (
          <>
            {view === "products" && (
            <section>
              {products.length ? (
                <div className="table-responsive">
                  <Table hover className="align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{translate("photo")}</th>
                        <th>{translate("product_name")}</th>
                        <th>{translate("category")}</th>
                        <th>{translate("material")}</th>
                        <th>{translate("annual_capacity")}</th>
                        <th>{translate("constructor")}</th>
                        <th>{translate("homologation")}</th>
                        <th>{translate("export")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((product) => {
                        const imageUrl = getProductImageUrl(normalizeImages(product.product_images)[0]);

                        return (
                          <tr key={product.id_product}>
                            <td>
                              {imageUrl ? (
                                <img src={imageUrl} alt="" className="asset-thumb" />
                              ) : (
                                <span className="asset-thumb-empty">
                                  <FaImage />
                                </span>
                              )}
                            </td>
                            <td className="text-value">{displayValue(product.product_name, missingText)}</td>
                            <td className="text-value">{displayValue(product.category, missingText)}</td>
                            <td className="text-value">{displayValue(product.material, missingText)}</td>
                            <td className="text-value">
                              {displayValue(product.annual_capacity, missingText)} {displayValue(product.unit, "")}
                            </td>
                            <td className="text-value">{displayValue(product.constructor_name, missingText)}</td>
                            <td>
                              {product.homologation_status ? (
                                <Badge bg="light" text="dark" className="border">
                                  {product.homologation_status}
                                </Badge>
                              ) : (
                                <span className="missing-chip">{missingText}</span>
                              )}
                            </td>
                            <td>
                              {product.export_status ? (
                                <Badge bg="light" text="dark" className="border">
                                  {product.export_status}
                                </Badge>
                              ) : (
                                <span className="missing-chip">{missingText}</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="border rounded-4 p-4 text-center text-value">
                  {translate("no_products_added")}
                </div>
              )}
            </section>
            )}

            {view === "certifications" && (
            <section>
              {certifications.length ? (
                <div className="table-responsive">
                  <Table hover className="align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{translate("certification")}</th>
                        <th>{translate("certificate_number")}</th>
                        <th>{translate("linked_product")}</th>
                        <th>{translate("expiry_date")}</th>
                        <th>{translate("status")}</th>
                        <th>{translate("export")}</th>
                        <th>{translate("document")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {certifications.map((certification) => {
                        const certificatePreview = buildPreviewableDocument(
                          certification.certificate_file,
                          certification.original_file_name ||
                            certification.certificate_number,
                          "certifications",
                          translate("certificate_file"),
                        );

                        return (
                          <tr key={certification.id}>
                            <td className="text-value">
                              {displayValue(certification.certification_name, missingText)}
                            </td>
                            <td className="text-value">
                              {displayValue(certification.certificate_number, missingText)}
                            </td>
                            <td className="text-value">
                              {displayValue(certification.linked_product, missingText)}
                            </td>
                            <td className="text-value">{displayValue(certification.expiry_date, missingText)}</td>
                            <td className="text-value">
                              {displayValue(certification.conformity_status, missingText)}
                            </td>
                            <td className="text-value">{displayValue(certification.export_impact, missingText)}</td>
                            <td>
                              {certificatePreview ? (
                                <Button
                                  type="button"
                                  onClick={() => setPreviewFile(certificatePreview)}
                                  size="sm"
                                  variant="outline-dark"
                                  className="d-inline-flex align-items-center gap-2"
                                >
                                  <FaEye />
                                  {translate("view")}
                                </Button>
                              ) : (
                                <span className="missing-chip">{missingText}</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="border rounded-4 p-4 text-center text-value">
                  {translate("no_certifications_available")}
                </div>
              )}
            </section>
            )}
          </>
        )}
      </div>
      <DocumentPreviewModal
        document={previewFile}
        fallbackTitle={translate("certificate_file")}
        onHide={() => setPreviewFile(null)}
        t={translate}
      />
    </div>
  );
}
